import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';

import { KeyPressInterface } from './key-press.interface';
import { isSpacer, isSpecial, notDisabledSpecialKeys, specialKeyIcons, specialKeyTexts } from './layouts';


/**
 * Key componente for a VirtualKeyboard
 *
 * @export
 * @class IonVirtualKeyboardKeyComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'ion-virtual-keyboard-key',
  template: `
    <button
      mat-raised-button2
      ion-button
      class="vk-button"
      [color]="special?'secondary':'light'"
      [style.width]="flexValue"
      [class.spacer]="spacer"
      [disabled]="isDisabled()"
      (click)="onKeyPress()"
    >
      <span *ngIf="!special">{{ keyValue }}</span>
    
      <span *ngIf="special">
        <ion-icon title="{{keyValue}}" [name]="icon"></ion-icon>
      </span>
    </button>
  `,
  styles: [`
    .mat-button,
    .mat-icon-button,
    .mat-raised-button {
      min-width: 64px;
      min-height: 64px;
      padding: 0;
      margin: 2px;
      font-size: 32px;
      line-height: 32px;
    }

    .vk-button{
      min-width: 64px;
      min-height: 48px;
      font-size: 20px;
      text-transform: none;
    }
    
    .vk-button.spacer,
    .mat-button.spacer,
    .mat-icon-button.spacer,
    .mat-raised-button.spacer {
      background-color: transparent;
      box-shadow: none;
    }
  `]
})
export class IonVirtualKeyboardKeyComponent implements OnInit {

  /**
   * Contains the key.
   *
   * @type {string}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  @Input('key') key: string = '';

  /**
   * Mark the key as disabled.
   *
   * @type {boolean}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  @Input('disabled') disabled: boolean = false;


  /**
   * Event launched when component is clicked.
   *
   * @memberof IonVirtualKeyboardKeyComponent
   */
  @Output() keyPress = new EventEmitter<KeyPressInterface>();


  /**
   * Is special key.
   *
   * @private
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private special = false;

  /**
   * Is spacer key.
   *
   * @private
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private spacer = false;


  /**
   * Specify the flex value.
   *
   * @private
   * @type {string}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private flexValue: string;

  /**
   * Value of the key.
   *
   * @private
   * @type {string}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private keyValue: string;

  /**
   * Icon of the key.
   *
   * @private
   * @type {string}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private icon: string;

  /**
   * Text of the key.
   *
   * @private
   * @type {string}
   * @memberof IonVirtualKeyboardKeyComponent
   */
  private text: string;

  /**
   * Constructor of the class.
   */
  public constructor() { }

  /**
   * On init life cycle hook, within this we'll initialize following properties:
   *  - special
   *  - keyValue
   *  - flexValue
   */
  public ngOnInit(): void {
    let multiplier = 1;
    let fix = 0;

    if (this.key.length > 1) {
      this.spacer = isSpacer(this.key);
      this.special = isSpecial(this.key);

      const matches = /^(\w+)(:(\d+(\.\d+)?))?$/g.exec(this.key);

      this.keyValue = matches[1];

      if (matches[3]) {
        multiplier = parseFloat(matches[3]);
        fix = (multiplier - 1) * 4;
      }
    } else {
      this.keyValue = this.key;
    }

    if (this.special) {
      if (specialKeyIcons.hasOwnProperty(this.keyValue)) {
        this.icon = specialKeyIcons[this.keyValue];
      } else if (specialKeyTexts.hasOwnProperty(this.keyValue)) {
        this.text = specialKeyTexts[this.keyValue];
      }
    }

    this.flexValue = `${multiplier * 64 + fix}px`;
  }

  /**
   * Method to check if key is disabled or not.
   *
   * @returns {boolean}
   */
  public isDisabled(): boolean {
    if (this.spacer) {
      return true;
    } else if (this.disabled && notDisabledSpecialKeys.indexOf(this.keyValue) !== -1) {
      return false;
    } else {
      return this.disabled;
    }
  }

  /**
   * Method to handle actual "key" press from virtual keyboard.
   *  1) Key is "Special", process special key event
   *  2) Key is "Normal", append this key value to input
   */
  public onKeyPress(): void {
    this.keyPress.emit({special: this.special, keyValue: this.keyValue, key: this.key});
  }
}



// WEBPACK FOOTER //
// ./src/modules/utils/ion-virtual-keyboard/ion-virtual-keyboard-key.component.ts